import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, notification } from "antd";

import { Loading } from "@/shared/components/Loading/Loading";
import { ADMIN_ROUTE, ASSESSMENT_TEMPLATES_ROUTE } from "@/routing/AppRouter/routes.constants";
import { AssessmentTemplateHttpService } from "@/modules/assessments/services/assessment-template-http.service";
import type { AssessmentTemplate } from "@/modules/assessments/types/template-creation.interface";
import { getFriendlyErrorMessage } from "@/shared/services/http";

import { VersionTemplateMetadataSection } from "./VersionTemplateMetadataSection";
import { TemplateForm, SubmitOutcome } from "./TemplateForm";
import { TemplateFormIntro } from "./TemplateFormIntro";
import { useTemplateTestDetails } from "./useTemplateTestDetails";
import { CREATED_URL_PARAM } from "../AssessmentTemplatesList";
import { ALL_CLIENTS, SelectedTest, TemplateFormData } from "./types";

const NOTES = [
  "This form should be used only after all test content, instructions, and norms that you want to use have already been created.",
  "This should only be used to replace an existing assessment template. Once a new version is created, the previous template will no longer be available when creating new assessments.",
];

interface CreateTemplateVersionFlowProps {
  templateId: number;
}

export function CreateTemplateVersionFlow({ templateId }: CreateTemplateVersionFlowProps) {
  const navigate = useNavigate();

  const [form] = Form.useForm<TemplateFormData>();
  const testDetails = useTemplateTestDetails(form);
  const { fetchTest } = testDetails;

  const [existingTemplate, setExistingTemplate] = useState<AssessmentTemplate | null>(null);
  const [isLoadingExistingTemplate, setIsLoadingExistingTemplate] = useState(true);
  const [existingTemplateError, setExistingTemplateError] = useState<string | null>(null);
  const isRedirectingAfterExistingTemplateError = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const loadTemplate = async () => {
      try {
        const template = await AssessmentTemplateHttpService.getTemplate(templateId);
        if (cancelled) return;
        setExistingTemplate(template);

        const sortedTests = [...template.tests].sort((a, b) => a.rank - b.rank);
        sortedTests.forEach((test) => fetchTest(test.testId));
      } catch (error) {
        if (cancelled) return;
        const message = getFriendlyErrorMessage(error) ?? "An unknown error occurred. Please try again.";
        setExistingTemplateError(message);
      } finally {
        if (!cancelled) setIsLoadingExistingTemplate(false);
      }
    };

    loadTemplate();

    return () => {
      cancelled = true;
    };
  }, [templateId, fetchTest]);

  useEffect(() => {
    if (isLoadingExistingTemplate || isRedirectingAfterExistingTemplateError.current) return;

    if (existingTemplateError || !existingTemplate) {
      isRedirectingAfterExistingTemplateError.current = true;
      notification.error({
        message: "There was an error loading this template",
        description: existingTemplateError,
      });
      navigate(`/${ADMIN_ROUTE}/${ASSESSMENT_TEMPLATES_ROUTE}`, { replace: true });
    }
  }, [isLoadingExistingTemplate, existingTemplateError, existingTemplate, navigate]);

  if (isLoadingExistingTemplate || existingTemplateError || !existingTemplate) {
    return <Loading />;
  }

  const sortedTests = [...existingTemplate.tests].sort((a, b) => a.rank - b.rank);
  const initialValues: Partial<TemplateFormData> = {
    templateName: existingTemplate.templateName,
    clientId: existingTemplate.clientId ?? ALL_CLIENTS,
    // Pre-populate the dragger with the source template's image as a URL-only UploadFile.
    // The dragger preview renders it from `url`; uploading a fresh file replaces it; clicking
    // the X drops the form value entirely.
    landingImageFile: existingTemplate.landingPageImageUrl
      ? {
          uid: "source-image",
          name: "current-landing-image",
          url: existingTemplate.landingPageImageUrl,
          status: "done",
        }
      : undefined,
    landingText: existingTemplate.landingText?.["en-US"] ?? "",
    tests: sortedTests.map<SelectedTest>((test) => ({
      testId: test.testId,
      normId: test.normId ?? undefined,
    })),
    formulas: "",
  };

  const handleSubmit = async (formValues: TemplateFormData): Promise<SubmitOutcome> => {
    const tests = formValues.tests.map((test, index) => {
      if (test.normId == null) {
        // list-level validators should have already blocked submit if any row is
        // missing a norm, but checking to avoid non-null assertion.
        throw new Error("Template version submission reached onFinish with a row missing a norm.");
      }
      return { testId: test.testId, normId: test.normId, rank: index + 1 };
    });

    const trimmedLandingText = (formValues.landingText ?? "").trim();
    const landingPageImage = formValues.landingImageFile?.originFileObj;
    const landingPageImageUrl =
      !landingPageImage && formValues.landingImageFile?.url ? formValues.landingImageFile.url : undefined;

    const result = await AssessmentTemplateHttpService.createTemplateVersion(existingTemplate.templateId, {
      templateName: existingTemplate.templateName,
      clientId: existingTemplate.clientId ?? undefined,
      landingPageText: trimmedLandingText ? { "en-US": trimmedLandingText } : undefined,
      tests,
      formulas: formValues.formulas ?? "",
      landingPageImage,
      landingPageImageUrl,
      zipFile: formValues.zipFile,
    });

    if (!result.success) {
      return { kind: "validation-failure", errors: result.errors };
    }

    const params = new URLSearchParams({
      [CREATED_URL_PARAM.TEMPLATE_NAME]: result.templateName,
      [CREATED_URL_PARAM.VERSION_NUMBER]: String(result.version),
    });
    if (existingTemplate.clientName) {
      params.set(CREATED_URL_PARAM.CLIENT_NAME, existingTemplate.clientName);
    }

    notification.success({
      message: `Version ${result.version} of "${result.templateName}" created successfully.`,
    });
    navigate(`/${ADMIN_ROUTE}/${ASSESSMENT_TEMPLATES_ROUTE}?${params.toString()}`);
    return { kind: "success" };
  };

  return (
    <TemplateForm
      pageTitle="New Assessment Template Version"
      submittingTitle="Creating Assessment Template Version"
      errorModalTitle="Assessment Template Version Creation Failed"
      form={form}
      testDetails={testDetails}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      <TemplateFormIntro introText="Use this form to create a new version of this assessment template." notes={NOTES} />
      <VersionTemplateMetadataSection
        templateName={existingTemplate.templateName}
        clientName={existingTemplate.clientName}
        nextVersion={existingTemplate.nextAvailableVersion}
      />
    </TemplateForm>
  );
}
